import React, { createContext, ReactNode, useMemo, useState } from "react";

import { OptionsModalType } from "../@types/Modal";

type ModalContextData = {
  isModalOpen: boolean;
  closeModal: () => void;
  modalContent: React.ReactNode | null;
  openModal: (
    content: React.ReactNode,
    optionsModal?: OptionsModalType
  ) => void;
  optionsModal: OptionsModalType;
  /** Merge into the open modal's options, e.g. to stop it being dismissed. */
  updateModalOptions: (options: Partial<OptionsModalType>) => void;
  sidePanelContent: React.ReactNode | null;
  sidePanelOptions: OptionsModalType;
  openSidePanel: (content: React.ReactNode, options?: OptionsModalType) => void;
  closeSidePanel: () => void;
};

type ModalProviderProps = {
  children: ReactNode;
};

export const ModalContext = createContext({} as ModalContextData);

export function ModalProvider({ children }: ModalProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [optionsModal, setOptionsModal] = useState({} as OptionsModalType);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null
  );
  const [sidePanelContent, setSidePanelContent] =
    useState<React.ReactNode | null>(null);
  const [sidePanelOptions, setSidePanelOptions] = useState(
    {} as OptionsModalType
  );

  function openModal(
    content: React.ReactNode,
    optionsModal?: OptionsModalType
  ) {
    if (optionsModal) {
      setOptionsModal(optionsModal);
    }

    setModalContent(content);
    setIsModalOpen(true);
  }

  function updateModalOptions(options: Partial<OptionsModalType>) {
    setOptionsModal((prev) => ({ ...prev, ...options }));
  }

  function closeModal() {
    setIsModalOpen(false);
    setModalContent(null);
    setSidePanelContent(null);
    setSidePanelOptions({} as OptionsModalType);
  }

  function openSidePanel(content: React.ReactNode, options?: OptionsModalType) {
    if (options) {
      setSidePanelOptions(options);
    }
    setSidePanelContent(content);
  }

  function closeSidePanel() {
    setSidePanelContent(null);
    setSidePanelOptions({} as OptionsModalType);
  }

  const modalValues = useMemo(() => {
    return {
      isModalOpen,
      modalContent,
      openModal,
      closeModal,
      optionsModal,
      updateModalOptions,
      sidePanelContent,
      sidePanelOptions,
      openSidePanel,
      closeSidePanel,
    };
    // modalContent/optionsModal were missing here, so opening a modal while
    // one was already open (isModalOpen unchanged) returned the cached value
    // and consumers never saw the new content.
  }, [
    isModalOpen,
    modalContent,
    optionsModal,
    sidePanelContent,
    sidePanelOptions,
  ]);

  return (
    <div>
      <ModalContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={modalValues}
      >
        {children}
      </ModalContext.Provider>
    </div>
  );
}
